'use client'

import { FormProvider, useForm } from 'form-components'
import { Input } from 'form-components/components/Input/Input'
import { SubmitButton } from 'form-components/components/SubmitButton/SubmitButton'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { GENERIC_INPUT_VALIDATION } from 'refunds/config/regexValidations'
import {
	MONEY_CASH_FIELDS,
	MONEY_CASH_FIELDS_ERRORS,
	MONEY_CASH_FIELDS_IDS,
	MONEY_CASH_FIELDS_LABELS,
	MONEY_CASH_FIELDS_VALIDATION,
} from 'refunds/constants/MoneyCashFields'
import { useSaveRefundData } from 'refunds/hooks/useSaveRefundData/useSaveRefundData'
import type { MoneyCashInfo, RefundData } from 'refunds/types/refundData'
import { useSession } from 'session/src/hooks/useSession'

import styles from './MoneyCashForm.module.scss'

interface MoneyCashFormProps {
	readonly onError?: () => void
	readonly onSaveData?: (values: MoneyCashInfo) => void
	readonly preloadData?: RefundData
}
export function MoneyCashForm({
	onError,
	onSaveData,
	preloadData,
}: MoneyCashFormProps) {
	const { t } = useLabels()
	const { email } = useSession()
	const {
		country: { countryISO },
	} = useMasterData()

	const { saveRefundData } = useSaveRefundData({ countryISO })

	const { moneyCash } = preloadData ?? {}
	const { moneyCashInfo } = moneyCash ?? {}

	const formMethods = useForm({
		defaultValues: { email, ...moneyCashInfo },
		mode: 'onTouched',
	})

	const { handleSubmit } = formMethods
	const validationRegex = new RegExp(GENERIC_INPUT_VALIDATION, 'u')

	const handleOnSubmit = async (formValues: MoneyCashInfo): Promise<void> => {
		if (preloadData) {
			const { isValid } = await saveRefundData({
				...preloadData,
				moneyCash: {
					selected: true,
					moneyCashInfo: {
						...moneyCash?.moneyCashInfo,
						...formValues,
					},
				},
			})

			if (isValid) {
				onSaveData?.(formValues)
				return
			}

			onError?.()
		}
	}

	return (
		<FormProvider {...formMethods}>
			<form
				data-testid='refunds.moneyCash.form'
				className={styles.form}
				onSubmit={handleSubmit(handleOnSubmit)}
			>
				<Input
					id={MONEY_CASH_FIELDS_IDS.FIRST_NAME}
					name={MONEY_CASH_FIELDS.FIRST_NAME}
					data-testid={MONEY_CASH_FIELDS_IDS.FIRST_NAME}
					label={t(MONEY_CASH_FIELDS_LABELS.FIRST_NAME)}
					labelFocused={t(MONEY_CASH_FIELDS_LABELS.FIRST_NAME)}
					errorMessage={t(MONEY_CASH_FIELDS_ERRORS.FIRST_NAME)}
					rules={{
						maxLength: MONEY_CASH_FIELDS_VALIDATION.MAX_LENGTH,
						required: true,
						regex: validationRegex,
					}}
				/>

				<Input
					id={MONEY_CASH_FIELDS_IDS.LAST_NAME}
					name={MONEY_CASH_FIELDS.LAST_NAME}
					data-testid={MONEY_CASH_FIELDS_IDS.LAST_NAME}
					label={t(MONEY_CASH_FIELDS_LABELS.LAST_NAME)}
					labelFocused={t(MONEY_CASH_FIELDS_LABELS.LAST_NAME)}
					errorMessage={t(MONEY_CASH_FIELDS_ERRORS.LAST_NAME)}
					rules={{
						maxLength: MONEY_CASH_FIELDS_VALIDATION.MAX_LENGTH,
						required: true,
						regex: validationRegex,
					}}
				/>

				<div>{t('form.refunds.cash.passport.text')}</div>

				<Input
					id={MONEY_CASH_FIELDS_IDS.MOBILE}
					name={MONEY_CASH_FIELDS.MOBILE}
					data-testid={MONEY_CASH_FIELDS_IDS.MOBILE}
					label={t(MONEY_CASH_FIELDS_LABELS.MOBILE)}
					labelFocused={t(MONEY_CASH_FIELDS_LABELS.MOBILE)}
					errorMessage={t(MONEY_CASH_FIELDS_ERRORS.MOBILE)}
					rules={{
						required: true,
						regex: validationRegex,
					}}
				/>

				<div>{t('form.refunds.cash.instructions.text')}</div>

				<Input
					id={MONEY_CASH_FIELDS_IDS.EMAIL}
					name={MONEY_CASH_FIELDS.EMAIL}
					data-testid={MONEY_CASH_FIELDS_IDS.EMAIL}
					rules={{
						required: true,
					}}
					disabled
				/>

				<div>{t('form.refunds.cash.confirmationEmail.text')}</div>

				<SubmitButton
					id='refunds.moneyCash.form.submit'
					data-testid='refunds.moneyCash.form.submit'
					className={styles.button}
				>
					{t('form.refunds.cash.save.button')}
				</SubmitButton>
			</form>
		</FormProvider>
	)
}
